import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { BORDER, COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { Button, DeleteIcon, Grid, P } from '@farewill/ui'

import { SHARED_PERSON_SCHEMA } from 'lib/probate/estate-questionnaire-schema'

import SharedContactsBanner from '../../shared-contacts/banner'
import { ignoreFailure } from '../../shared-contacts/helpers'
import { useEstatePeople } from '../../shared-contacts/estate-people'
import { Role } from '../../shared-contacts/roles'
import SharedPersonCard from '../../shared-contacts/shared-person-card'
import useSharedPeople from '../../shared-contacts/use-shared-people'

const StyledCoOwner = styled.div`
  padding: ${GTR.S};
  border: 1px solid ${COLOR.GREY.LIGHT};
  border-radius: ${BORDER.RADIUS.S};
`

const StyledCoOwnerHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${GTR.XS};
`

const StyledAddButton = styled(Button.Secondary)`
  padding: ${GTR.XXS} ${GTR.S};
  font-size: ${FONT.SIZE.S};
`

type Props = {
  coOwnerIds: number[]
  saveCoOwnerIds: (coOwnerIds: number[]) => Promise<unknown>
}

const CoOwners = ({ coOwnerIds, saveCoOwnerIds }: Props) => {
  const { createPerson } = useEstatePeople()

  // from the first co-owner being saved until the property shows them
  const [isLinking, setIsLinking] = useState(false)
  useEffect(() => {
    if (coOwnerIds.length) setIsLinking(false)
  }, [coOwnerIds.length])

  const link = async <Result,>(save: () => Promise<Result>) => {
    setIsLinking(true)
    try {
      return await save()
    } catch (error) {
      setIsLinking(false)
      throw error
    }
  }

  const role: Role = {
    memberIds: coOwnerIds,
    add: (personId) => link(() => saveCoOwnerIds([...coOwnerIds, personId])),
    remove: (personId) =>
      saveCoOwnerIds(coOwnerIds.filter((id) => id !== personId)),
    replace: (fromId, toId) =>
      saveCoOwnerIds(coOwnerIds.map((id) => (id === fromId ? toId : id))),
    addNew: (attributes) =>
      link(async () => {
        const coOwner = await createPerson(attributes)
        await saveCoOwnerIds([...coOwnerIds, coOwner.id])
        return coOwner.id
      }),
  }

  const { records, hasSharedContacts, addDraft, remove, cardProps } =
    useSharedPeople(role)

  // a jointly owned property has someone else owning it, so there's always a
  // card to fill in. Not while the first co-owner is being saved, nor while
  // the property's co-owners are still waiting on the estate's contacts
  const needsDraft =
    records.length === 0 && coOwnerIds.length === 0 && !isLinking
  useEffect(() => {
    if (needsDraft) addDraft()
  }, [needsDraft, addDraft])

  return (
    <>
      {hasSharedContacts && (
        <Grid.Item span={12}>
          <SharedContactsBanner />
        </Grid.Item>
      )}
      {records.map((record, index) => (
        <Grid.Item span={12} key={record.slot}>
          <StyledCoOwner>
            <StyledCoOwnerHeading>
              <P size="S" strong margin={0}>
                Co-owner {index + 1}
              </P>
              <Button.Plain
                type="button"
                aria-label={`Remove co-owner ${index + 1}`}
                onClick={() => ignoreFailure(remove(record.id))}
              >
                <DeleteIcon size="S" />
              </Button.Plain>
            </StyledCoOwnerHeading>
            <SharedPersonCard
              // a new form for whoever's in the card, in the same card
              key={record.id}
              compact
              schema={SHARED_PERSON_SCHEMA}
              {...cardProps(record)}
            />
          </StyledCoOwner>
        </Grid.Item>
      ))}
      <Grid.Item span={12}>
        <StyledAddButton
          type="button"
          size="S"
          disabled={records.some((record) => record.isDraft)}
          onClick={addDraft}
        >
          Add co-owner
        </StyledAddButton>
      </Grid.Item>
    </>
  )
}

export default CoOwners
